import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
} from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { takeUntil } from "rxjs/internal/operators/takeUntil";
import { Subject } from "rxjs/internal/Subject";
import { distinctUntilChanged, map } from "rxjs/operators";
import { ProductosService } from "src/app/business/productos.service";
import { TranslateService } from "src/app/shared/translate.service";
import { DatosFactura } from "../../interfaces/resume.interface";
import { DatosFacturaInputNameTypesEnum, FacturaFormDataValue, TyposDeInputEnam, DatosCiudades, ClientFormDataValue, DatosCompraInputNamesEnum, DatosPaises } from "src/app/business/interfaces/configuraciones";
import { PaymentService } from "src/app/core/payment/payment.service";
import { UtilsService } from "src/app/business/utils.service";

export const alphaNumWithSpaces = Validators.pattern('^[A-Za-z0-9ñÑáéíóúÁÉÍÓÚ. ]+$');
export const alphaWithSpaces = Validators.pattern('^[A-Za-zñÑáéíóúÁÉÍÓÚ. ]+$');

@Component({
  selector: "app-factura",
  templateUrl: "./factura.component.html",
  styleUrls: ["./factura.component.scss"],
})
export class FacturaComponent implements OnInit, OnDestroy, OnChanges {
  private ngUnsubscribe = new Subject();

  check = false;
  datosFacturaForm: FormGroup;
  @Input() paisesV2: DatosPaises[];
  @Input() provinciasV2: DatosCiudades[];
  @Input() ciudadesV2: DatosCiudades[];
  @Input() clientForm: FormGroup;
  @Output() facturaForm = new EventEmitter<FormGroup>();
  @Output() isRequestFacturaChecked = new EventEmitter<boolean>();
  
  lastSelectedCodigoPais = "";
  
  datosFactura: DatosFactura[];
  facturaInputNameTypesEnum = DatosFacturaInputNameTypesEnum;
  inputTypesEnum = TyposDeInputEnam;
  lastSelectedProvincia = "";
  codProvincia: string = "";
  codCiudad: string = "";
  clientFormValue: ClientFormDataValue;
  prevClientFormValues: ClientFormDataValue;
  clientFormInputNamesEnum = DatosCompraInputNamesEnum;
  paisDefault: string = sessionStorage.getItem('paisDefault') || '';

  isToggleFacturaVisible: boolean = false;
  clientByAdmin: any = null;
  isSolicitaFactura: number = 0;
  isPanelVisible: boolean = false;
  isPosibleConfigurarDatos: boolean = false;
  generateFacturaType: number = 0;
  isFillDataChecked: boolean = false;
  isFirstLoad: boolean = true;
  utilizarDatosClientesConf: DatosFactura;

  constructor(
    private productosService: ProductosService,
    protected translateService: TranslateService,
    private paymentService: PaymentService,
    private utilService: UtilsService,
    private fb: FormBuilder,
  ) {}

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnInit() {
    this.productosService.getFormularioCompra({ enlaceMenuId: this.productosService.getEnlaceMenuId()?.pkid })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((formularioCompra) => {
          const facturaConfig = formularioCompra.datosFactura;
          this.datosFactura = facturaConfig;
          this.utilizarDatosClientesConf = facturaConfig.find(conf => conf.name === this.facturaInputNameTypesEnum.UTILIZAR_DATOS_CLIENTE);
          const findUtilizarDatosClienteCheckbox = facturaConfig.find(factura => factura.name === this.facturaInputNameTypesEnum.UTILIZAR_DATOS_CLIENTE);
          this.isFillDataChecked = findUtilizarDatosClienteCheckbox.value;
          this.setFacturaConfigData();
          this.check = this.isPanelVisible;
          this.isRequestFacturaChecked.next(this.check);
          
          this.datosFacturaForm = this.buildForm(this.datosFactura);
          this.isPosibleConfigurarDatos ? this.datosFacturaForm.enable() : this.datosFacturaForm.disable();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const getClientFormValues = sessionStorage.getItem('cliente');

    // Emmit and fill factura form after reload page and take data from client form
    if (changes['clientForm']) {
      this.clientFormValue = changes['clientForm'].currentValue?.value?.items as ClientFormDataValue;
    }

    if (changes['clientForm'] && this.isFirstLoad && getClientFormValues) {
      this.clientFormValue = this.clientForm?.value?.items; 
      
      if (this.datosFacturaForm && this.isToggleFacturaVisible && this.isFillDataChecked) {
        this.fillDataFormFromClientForm();
      }

      if (this.isToggleFacturaVisible) {
        this.emmitFacturaForm();
      }

    }

    if (changes['clientForm'] || changes['provinciasV2'] || changes['ciudadesV2'] || changes['paisesV2']) {
      this.clientForm?.valueChanges
        .pipe(
          map(form => form.items),
          // Remove privacidad and publicidad checkbox for avoid trigger subscribe when value changes on checkbox
          map(({ privacidad, publicidad, ...rest }) => rest),
          distinctUntilChanged((prev, next) =>{
            this.prevClientFormValues = prev;

            return JSON.stringify(prev) === JSON.stringify(next);
          }),
          takeUntil(this.ngUnsubscribe), 
        )
        .subscribe((values: ClientFormDataValue) => {
          this.clientFormValue = values;

          if (this.isToggleFacturaVisible && this.isFillDataChecked) {
            this.fillDataFormFromClientForm();
          }

          if (this.isToggleFacturaVisible) {
            this.emmitFacturaForm();
          }
        })
    }
  }

  public get datosFacturaFormControls() {
    return this.datosFacturaForm.controls;
  }

  public emitValues = () => {
    this.emmitFacturaForm();
  }

  public typoDocumentoSelectChange(value: string): void {
    this.datosFacturaFormControls.documento.clearValidators();
    this.datosFacturaFormControls.documento.setValidators([Validators.pattern(this.utilService.documentoValidar(value)), Validators.required]);
    this.datosFacturaFormControls.documento.updateValueAndValidity();
  }

  public isShowFactura(): void {
    const isChecked = !this.check;
    
    this.isRequestFacturaChecked.next(isChecked);
    this.isPanelVisible = isChecked;
    this.check = isChecked;
    this.datosFacturaForm.controls.isPanelVisible.setValue(isChecked);
    this.paymentService.setDatosFacturaValue(this.datosFacturaForm.value);
    
    if (isChecked && this.isFillDataChecked) {
      this.fillDataFormFromClientForm()
    }
  }

  public fillDataFromCompra(): void {
    const isChecked = !this.isFillDataChecked;
    this.isFillDataChecked = isChecked;
    this.datosFacturaFormControls.UtilizarDatosCliente.patchValue(isChecked);
    
    if(isChecked){
      this.isFirstLoad = isChecked;
      this.fillDataFormFromClientForm();
    } else {
      this.clearInputByRefname();
      this.datosFacturaFormControls.UtilizarDatosCliente.markAsTouched();
      this.datosFacturaFormControls.UtilizarDatosCliente.updateValueAndValidity();
    }
  }

  private fillDataFormFromClientForm(): void {
    this.datosFacturaForm.patchValue({
      ...this.updateValueByReferenceName(),
      isPanelVisible: this.isPanelVisible,
      isSolicitaFactura: this.isSolicitaFactura,
    }, { emitEvent: true });

    if (this.datosFacturaForm.invalid) {
      this.validateInputByName();
      this.datosFacturaFormControls.documento.clearValidators();
      this.datosFacturaFormControls.documento.setValidators(
        [Validators.pattern(this.utilService.documentoValidar(this.datosFacturaForm.controls[this.facturaInputNameTypesEnum.TIPO_DOCUMENTO].value)), Validators.required]
      );
      this.datosFacturaFormControls.UtilizarDatosCliente.markAsTouched();
      this.datosFacturaFormControls.UtilizarDatosCliente.updateValueAndValidity();
    }
  }

  private setFacturaConfigData(): void {
    this.isSolicitaFactura = +this.productosService.getEnlaceMenuId()?.iSolicitaFactura;
    this.isToggleFacturaVisible = this.isSolicitaFactura === 0 ? false : true,
    this.isPanelVisible = this.isSolicitaFactura === 2 || this.isSolicitaFactura === 3 || this.isSolicitaFactura === 5;
    this.isPosibleConfigurarDatos = this.isSolicitaFactura !== 3;
    this.generateFacturaType = this.isSolicitaFactura !== 0 && this.isSolicitaFactura !== 4 ? 1 : 2;
  }

  private buildForm(datosFactura: DatosFactura[]): FormGroup {
    const dynamicForm = this.fb.group({});
    const getPaisByNombre = this.paisesV2.length && this.paisesV2?.find(pais => pais.NombrePais === this.paisDefault);
        
    datosFactura.forEach(input => {
      const value = typeof input.value === 'boolean' ? input.value : input.value || '';
      const control = new FormControl(value, this.getValidatorForInput(input));
      dynamicForm.addControl(input.name, control);

      if (input.name === this.facturaInputNameTypesEnum.PAIS) {
        control.setValue(getPaisByNombre?.idPais || '');
      }
    })

    dynamicForm.addControl('isPanelVisible', new FormControl(this.isPanelVisible));
    dynamicForm.addControl('iSolicitaFactura', new FormControl(this.isSolicitaFactura));
    dynamicForm.addControl('generateFacturaType', new FormControl(this.generateFacturaType));
    dynamicForm.addControl('nombrePais', new FormControl(getPaisByNombre?.NombrePais || ''));
    
    return dynamicForm;
  }

  private getValidatorForInput(input: DatosFactura) {
    const validators = [];
    if (input.required && typeof input.value !== 'boolean') {
      validators.push(Validators.required);
    }

    if (input.required && typeof input.value === 'boolean') {
      validators.push(Validators.requiredTrue);
    }

    if(input.inputType === this.inputTypesEnum.TEXT && input.name !== this.facturaInputNameTypesEnum.NOMBRE) {
      validators.push(Validators.maxLength(input.maximo));
      validators.push(alphaNumWithSpaces);
    }

    if(input.inputType === this.inputTypesEnum.TEXT && input.name === this.facturaInputNameTypesEnum.NOMBRE) {
      validators.push(Validators.maxLength(input.maximo));
      validators.push(alphaWithSpaces);
    }

    return validators;
  }

  private updateValueByReferenceName(): FacturaFormDataValue {
    let changedKeys = [];

    if (this.isFirstLoad) {
      changedKeys = Object.keys(this.clientFormValue || {}).filter(key => this.clientFormValue[key] !== undefined);
      this.isFirstLoad = this.prevClientFormValues ? false : true;
    } else {
      changedKeys = Object.keys(this.clientFormValue || {}).filter(key => this.prevClientFormValues && this.clientFormValue[key] !== this.prevClientFormValues[key]);
    }

    if (!changedKeys.length) return { ...this.datosFacturaForm.value };

    const referencedKyes = this.datosFactura.filter(factura => {
      const refeNames = factura.referenceName.split(',');

      return changedKeys.some(key => refeNames.includes(key) 
          || (key === this.clientFormInputNamesEnum.CODIGO_PAIS 
          && refeNames.includes(this.clientFormInputNamesEnum.PAIS))
        ) && factura.referenceDepedence === this.facturaInputNameTypesEnum.UTILIZAR_DATOS_CLIENTE;
    })

    if (!referencedKyes.length) return { ...this.datosFacturaForm.value };

    const getUpdatedValues = referencedKyes.reduce((data: FacturaFormDataValue, factura) => {
      const refeNames = factura.referenceName.split(',');
      const concatLabeles = refeNames.length > 1 ? this.concatLabels(refeNames).trim() : null;

      if (        
        factura.name !== this.facturaInputNameTypesEnum.PAIS && 
        factura.name !== this.facturaInputNameTypesEnum.PROVINCIA && 
        factura.name !== this.facturaInputNameTypesEnum.LOCALIDAD && 
        factura.visible
      ) {
        data[factura.name] = concatLabeles || this.clientFormValue[refeNames[0]];
      }

      if (factura.name === this.facturaInputNameTypesEnum.PAIS && factura.visible) {
        const getPaisById = this.paisesV2.length && this.paisesV2?.find(
          pais => pais.idPais === this.clientFormValue.codigoPais 
            || pais.NombrePais === this.clientFormValue.pais);
            
        data[factura.name] = getPaisById.idPais || this.clientFormValue.pais || '';
        data['nombrePais'] = getPaisById.NombrePais;
      }

      if (factura.name === this.facturaInputNameTypesEnum.PROVINCIA && factura.visible) {
        const getProvinciaByCode = this.ciudadesV2.length ? this.ciudadesV2?.find(
          ciudad => ciudad.CodProvincia === this.clientFormValue.provincia 
            || ciudad.NombreProvincia === this.clientFormValue.provincia)?.CodProvincia 
            : this.clientFormValue.provincia;

        data[factura.name] = getProvinciaByCode || '';
      }

      if (factura.name === this.facturaInputNameTypesEnum.LOCALIDAD && factura.visible) {
        const getCiudadByCode = this.ciudadesV2.length ? 
          this.ciudadesV2?.find(ciudad => 
            ciudad.CodCiudad === this.clientFormValue?.localidad 
            || ciudad.NombreCiudad === this.clientFormValue?.localidad 
            || ciudad.CodCiudad === this.clientFormValue?.ciudad 
            || ciudad.NombreCiudad === this.clientFormValue?.ciudad)?.CodCiudad 
            : (this.clientFormValue?.localidad || this.clientFormValue?.ciudad);

          data[factura.name] = getCiudadByCode || '';
      }

      return data;
    }, {} as FacturaFormDataValue);

    return { ...this.datosFacturaForm.value, ...getUpdatedValues }
  }

  private concatLabels(refNames: string[]): string {
    return refNames.reduce((val, currName) => {
      if (this.clientFormValue[currName]) {
        val += `${this.clientFormValue[currName]} `;
      }
      return val;
    }, '')
  }

  private validateInputByName(): void {
    this.datosFactura.forEach((factura) =>  {
      const refeNames = factura.referenceName.split(',');
      const isDependeseToClientForm = Object.keys(this.clientFormValue || {}).some(key => refeNames.includes(key)) && factura.referenceDepedence === this.facturaInputNameTypesEnum.UTILIZAR_DATOS_CLIENTE;

      if (refeNames.length && refeNames.length > 1 && isDependeseToClientForm && (refeNames.includes('nombre') || refeNames.includes('Nombrecontacto') || refeNames.includes('apellidos'))) {
        if (this.clientFormValue?.Nombrecontacto || this.clientFormValue?.nombre || this.clientFormValue?.apellidos) {
          this.datosFacturaForm.get(this.facturaInputNameTypesEnum.NOMBRE).markAsDirty();
          this.datosFacturaForm.get(this.facturaInputNameTypesEnum.NOMBRE).markAsTouched();
        }
      }
      
      if (refeNames.length && isDependeseToClientForm && !refeNames.includes('nombre') && !refeNames.includes('apellidos')) {
        if(this.clientFormValue[refeNames[0]]) {
          this.datosFacturaForm.get(factura.name).markAsDirty();
          this.datosFacturaForm.get(factura.name).markAsTouched();
        }
      }
    });
  }

  private clearInputByRefname(): void {
    this.datosFactura.forEach((factura) =>  {
      const refeNames = factura.referenceName.split(',');
      const isDependeseToClientForm = Object.keys(this.clientFormValue || {}).some(key => refeNames.includes(key)) && factura.referenceDepedence === this.facturaInputNameTypesEnum.UTILIZAR_DATOS_CLIENTE;
      
      if (refeNames.length && refeNames.length > 1 && isDependeseToClientForm && (refeNames.includes('nombre') || refeNames.includes('Nombrecontacto') || refeNames.includes('apellidos'))) {
        if (this.clientFormValue?.Nombrecontacto || this.clientFormValue?.nombre || this.clientFormValue?.apellidos) {
          this.datosFacturaForm.get(this.facturaInputNameTypesEnum.NOMBRE).reset('');

          if (factura.required) {
            this.datosFacturaForm.get(this.facturaInputNameTypesEnum.NOMBRE).markAsDirty();
            this.datosFacturaForm.get(this.facturaInputNameTypesEnum.NOMBRE).markAsTouched();
          } else {
            this.datosFacturaForm.get(this.facturaInputNameTypesEnum.NOMBRE).markAsPristine();
          }
        }
      }
      
      if (refeNames.length && isDependeseToClientForm && !refeNames.includes('nombre') && !refeNames.includes('apellidos')) {
        if(this.clientFormValue[refeNames[0]]) {
          this.datosFacturaForm.get(factura.name).reset('');

          if (factura.required) {
            this.datosFacturaForm.get(factura.name).markAsDirty();
            this.datosFacturaForm.get(factura.name).markAsTouched();
          } else {
            this.datosFacturaForm.get(factura.name).markAsPristine();
          }
        }
      }
    });
  }

  private emmitFacturaForm(): void {
    const findPais = this.paisesV2.find(pais => pais.idPais === this.datosFacturaForm.controls['pais'].value);
    this.datosFacturaForm.controls['nombrePais'].setValue(findPais?.NombrePais || '');
    
    this.facturaForm.emit(this.datosFacturaForm);
    this.paymentService.setDatosFacturaValue(this.datosFacturaForm.value);
  }
}
